'use client';

import { ChevronDown, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';

import {
  getDataGridMinWidth,
  getVisibleDataColumns,
} from '../bulk-edit-columns';
import { useBulkEditLayoutMetrics } from '../bulk-edit-layout-context';
import { bulkEditCheckbox, bulkEditTableScroll } from '../bulk-edit-styles';
import { getBulkEditColumnLabel, isCellDirty, isVariantFieldDisabled } from '../bulk-edit.utils';
import type { BulkEditRow } from '../bulk-edit.types';
import type { BulkEditState } from '../hooks/use-bulk-edit-state';
import { BulkEditRowFields } from './bulk-edit-row-fields';
import { BulkEditTableSkeleton } from './bulk-edit-table-skeleton';
import { BulkEditThumbUpload } from './bulk-edit-thumb-upload';

interface BulkEditTableProps {
  state: BulkEditState;
}

function BulkEditTreeSlot({
  row,
  onToggleExpand,
}: {
  row: BulkEditRow;
  onToggleExpand: (productId: string) => void;
}) {
  const isProduct = row.kind === 'product';
  const isVariant = row.kind === 'variant';
  const hasChildren = isProduct && row.variantCount > 1;
  const showStem = hasChildren && row.isExpanded;

  if (isVariant) {
    return (
      <div
        className={cn(
          'crm-bulk-edit-tree-slot',
          row.isLastVariantInGroup
            ? 'crm-bulk-edit-tree-slot--branch-last'
            : 'crm-bulk-edit-tree-slot--branch',
        )}
        aria-hidden
      />
    );
  }

  return (
    <div
      className={cn(
        'crm-bulk-edit-tree-slot',
        hasChildren && 'crm-bulk-edit-tree-slot--parent',
        showStem && 'crm-bulk-edit-tree-slot--stem',
      )}
    >
      {hasChildren ? (
        <button
          type="button"
          className="crm-bulk-edit-expand-btn"
          onClick={() => onToggleExpand(row.productId)}
          aria-expanded={row.isExpanded}
        >
          {row.isExpanded ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
        </button>
      ) : null}
    </div>
  );
}

function BulkEditProductCell({
  row,
  onToggleExpand,
  onUpdateName,
  onUpdateImage,
}: {
  row: BulkEditRow;
  onToggleExpand: (productId: string) => void;
  onUpdateName: (name: string) => void;
  onUpdateImage: (url: string) => void;
}) {
  const isProduct = row.kind === 'product';
  const isVariant = row.kind === 'variant';
  const imageDisabled = isVariantFieldDisabled(isProduct, row.hasChildVariants);

  return (
    <div
      className={cn(
        'crm-bulk-edit-product-body',
        isVariant && 'crm-bulk-edit-product-body--variant',
      )}
    >
      <BulkEditTreeSlot row={row} onToggleExpand={onToggleExpand} />

      <div className="crm-bulk-edit-product-main">
        <BulkEditThumbUpload
          imageUrl={row.imageUrl}
          disabled={imageDisabled}
          onImageAvailable={onUpdateImage}
        />

        <input
          type="text"
          value={row.name}
          onChange={(e) => onUpdateName(e.target.value)}
          className={cn(
            'crm-bulk-edit-name',
            isProduct ? 'crm-bulk-edit-name--product' : 'crm-bulk-edit-name--variant',
          )}
        />
      </div>
    </div>
  );
}

function BulkEditGridRow({
  row,
  state,
  isHeader,
}: {
  row?: BulkEditRow;
  state: BulkEditState;
  isHeader?: boolean;
}) {
  const { t } = useTranslation(['products', 'common']);
  const {
    selectedKeys,
    visibleColumns,
    edits,
    toggleExpanded,
    toggleRowSelected,
    updateCell,
    storeCurrencies,
    isCreditEnabled,
  } = state;
  const { rowPx, headerPx, checkboxPx, productPx, leftPx } = useBulkEditLayoutMetrics();

  const dataColumns = getVisibleDataColumns(visibleColumns);
  const rowSelected = row ? selectedKeys.has(row.key) : false;
  const productColDirty =
    row != null &&
    (isCellDirty(row.key, 'name', edits) || isCellDirty(row.key, 'imageUrl', edits));

  return (
    <div
      className={cn(
        'crm-bulk-edit-row flex w-max min-w-full',
        isHeader && 'crm-bulk-edit-row--head',
        !isHeader && row?.kind === 'variant' && 'crm-bulk-edit-row--variant',
        !isHeader && rowSelected && 'crm-bulk-edit-row--selected',
      )}
      style={{ height: isHeader ? headerPx : rowPx }}
    >
      <div className={cn('crm-bulk-edit-frozen flex h-full shrink-0')} style={{ width: leftPx }}>
        <div
          className="crm-bulk-edit-cell crm-bulk-edit-cell--checkbox flex shrink-0 items-center justify-center"
          style={{ width: checkboxPx }}
        >
          {isHeader ? (
            <Checkbox
              className={bulkEditCheckbox}
              checked={
                state.rows.length > 0 && state.rows.every((r) => selectedKeys.has(r.key))
                  ? true
                  : state.rows.some((r) => selectedKeys.has(r.key))
                    ? 'indeterminate'
                    : false
              }
              onCheckedChange={(v) => state.toggleAllSelected(!!v)}
              aria-label={t('products:table.selectAll')}
            />
          ) : row ? (
            <Checkbox
              className={bulkEditCheckbox}
              checked={selectedKeys.has(row.key)}
              onCheckedChange={(v) => toggleRowSelected(row.key, !!v)}
              aria-label={row.name}
            />
          ) : null}
        </div>

        <div
          className={cn(
            'crm-bulk-edit-cell crm-bulk-edit-product-col flex h-full min-w-0 flex-1',
            productColDirty && 'crm-bulk-edit-cell--dirty',
          )}
          style={{ width: productPx }}
        >
          {isHeader ? (
            <div className="crm-bulk-edit-th crm-bulk-edit-product-th h-full w-full">
              {t('products:bulkEditor.columns.title')}
            </div>
          ) : row ? (
            <BulkEditProductCell
              row={row}
              onToggleExpand={toggleExpanded}
              onUpdateName={(name) => updateCell(row.key, { name })}
              onUpdateImage={(url) => updateCell(row.key, { imageUrl: url })}
            />
          ) : null}
        </div>
      </div>

      <div className="crm-bulk-edit-data-cols flex shrink-0">
        {isHeader
          ? dataColumns.map((col) => (
              <div
                key={col.id}
                className={cn(
                  'crm-bulk-edit-th crm-bulk-edit-cell shrink-0',
                  col.align === 'right' && 'justify-end text-right',
                )}
                style={{ width: col.width, height: headerPx }}
              >
                {getBulkEditColumnLabel(t, col.id)}
              </div>
            ))
          : row
            ? dataColumns.map((col) => (
                <BulkEditRowFields
                  key={`${row.key}-${col.id}`}
                  row={row}
                  column={col}
                  edits={edits}
                  storeCurrencies={storeCurrencies}
                  t={t}
                  isCreditEnabled={isCreditEnabled}
                  onUpdate={(patch) => updateCell(row.key, patch)}
                />
              ))
            : null}
      </div>
    </div>
  );
}

export function BulkEditTable({ state }: BulkEditTableProps) {
  const { t } = useTranslation(['products']);
  const { rows, visibleColumns, isLoading } = state;
  const { leftPx } = useBulkEditLayoutMetrics();

  const gridMinWidth = leftPx + getDataGridMinWidth(visibleColumns);
  const showSkeleton = isLoading && rows.length === 0;

  if (showSkeleton) {
    return <BulkEditTableSkeleton visibleColumns={visibleColumns} />;
  }

  if (rows.length === 0) {
    return (
      <div className={`${bulkEditTableScroll} flex items-center justify-center`}>
        <p className="text-sm text-muted-foreground">{t('products:noProductsFound')}</p>
      </div>
    );
  }

  return (
    <div className={bulkEditTableScroll}>
      <div style={{ minWidth: gridMinWidth }}>
        <BulkEditGridRow state={state} isHeader />
        {rows.map((row) => (
          <BulkEditGridRow key={row.key} row={row} state={state} />
        ))}
      </div>
    </div>
  );
}
